/**
 * Default Collection Attributes.
 * @const DefaultCollection
 * @example {
 * sort: string,
 * order_by: string,
 * per_page: number,
 * current_page: number,
 * last_page: number,
 * total: number,
 * from: number,
 * to: number,
 * data: Array,
 * per_page_options: number[]
 * }
 */
const DefaultCollection = {
    sort: 'desc',
    order_by: 'id',
    per_page: 15,
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 1,
    to: 1,
    data: [],
    filters: {},
    per_page_options: [5, 15, 25, 50, 100],
    queryParams: {
        current_page: 'page',
        per_page: 'per_page',
        order_by: 'sort',
        sort: 'dir',
        filters: 'filter',
    },
    params: {

    },
}


/**
 * Collection Class
 * Laravel Paginator Compatible Schema
 */
class Collection {

    /**
     * Class Constructor
     * @param state @type {{}|DefaultCollection}
     */
    constructor(state = {}) {
        this.state = Object.assign({}, DefaultCollection, state)
    }

    get isApiResource() {
        return !!this.get('meta');
    }

    /**
     * (Getter) pagination Links
     * @return {Array}
     */
    get pagination() {
        return this.buildLinks(this.get('current_page'), this.get('last_page'))
    }
    /**
     * (Getter) urlParams
     * @return {String}
     */
    get urlParams() {
        let queryParams = this.get('queryParams');
        let params = {}
        params[queryParams.per_page] = this.perPage;
        params[queryParams.current_page] = this.currentPage;
        params[queryParams.order_by] = this.orderBy;
        params[queryParams.sort] = this.sort;
        return params;
    }

    get filtersParam() {
        let queryParams = this.get('queryParams');
        let params = {}
        params[queryParams.filters] = this.filters;
        return params;
    }

    get allUrlParams() {
        // let queryParams = this.get('queryParams');
        let params = Object.assign({}, this.urlParams, this.filtersParam);
        return params;
    }

    get filters() {
        let filters = {};
        let f = this.state.filters;
        for ( let key in f ) {
            let val = f[key];
            if(val
                || (val && typeof val === 'string' || val instanceof String)
                || ((val !== undefined || val !== null) && (typeof val === 'number' || val instanceof Number))){
                filters[key] = val;
            }
        }
        return filters;
    }

    /**
     * (Getter) items
     * @return {Array}
     */
    get items() {
        return this.get('data')
    }

    /**
     * (Getter) actions
     * @return {Array}
     */
    get actions() {
        return this.get('actions')
    }

    /**
     * (Method) Get Item
     * @param index @type {Number}
     * @return {Object}
     */
    item(idx) {
        return this.state.data[idx] ? this.state.data[idx] : null;
    }
    /**
     * (Getter) hasItems
     * @return {Boolean}
     */
    get hasItems() {
        return this.items.length > 0
    }
    /**
     * (Getter) from
     * @return {Number}
     */
    get from() {
        return this.get('from')
    }
    /**
     * (Getter) to
     * @return {Number}
     */
    get to() {
        return this.get('to')
    }
    /**
     * (Getter) total
     * @return {Number}
     */
    get total() {
        return this.get('total')
    }
    /**
     * (Getter) lastPage
     * @return {Number}
     */
    get lastPage() {
        return this.get('last_page')
    }
    /**
     * (Getter) currentPage
     * @return {Number}
     */
    get currentPage() {
        return this.get('current_page')
    }
    /**
     * (Setter) currentPage
     * @param value @type {Number}
     * @return void
     */
    set currentPage(value) {
        this.set('current_page', value)
    }
    /**
     * (Getter) orderBy
     * @return {String}
     */
    get orderBy() {
        return this.get('order_by')
    }
    /**
     * (Setter) orderBy
     * @param value @type {String}
     * @return void
     */
    set orderBy(value) {
        this.set('order_by', value)
    }
    /**
     * (Getter) sort
     * @return {String}
     */
    get sort() {
        return this.get('sort')
    }
    /**
     * (Setter) sort
     * @param value @type {String}
     * @return void
     */
    set sort(value) {
        this.set('sort', value)
    }
    /**
     * (Getter) perPage
     * @return {Number}
     */
    get perPage() {
        return this.get('per_page')
    }
    /**
     * (Setter) sort
     * @param value @type {Number}
     * @return void
     */
    set perPage(value) {
        this.setState({
            per_page: this.get('per_page_options').includes(value) ? value : 5,
            current_page: 1
        })
    }
    /**
     * (Getter) itemCount
     * @return {Number}
     */
    get perPageOptions() {
        return this.get('per_page_options')
    }

    /**
     * (Getter) hasPagination
     * @return {Boolean}
     */
    get hasPagination() {
        return this.get('last_page') > 1
    }
    /**
     * (Getter) shouldRenderLinks
     * @return {Boolean}
     */
    get shouldRenderLinks() {
        return this.pagination.includes(this.get('current_page'))
    }
    /**
     * (Getter) hasNextPage
     * @return {Boolean}
     */
    get hasNextPage() {
        return this.get('current_page') + 1 <= this.get('last_page')
    }
    /**
     * (Getter) hasPreviousPage
     * @return {Boolean}
     */
    get hasPreviousPage() {
        return this.get('current_page') - 1 >= 1
    }
    /**
     * (Method) Get Field Value
     * @param field @type {String}
     * @return {*}
     */
    get(field) {
        return this.state[field]
    }
    /**
     * (Method) Set Field
     * @param field @type {String}
     * @param value @type {*}
     * @return {this}
     */
    set(field, value) {
        this.state[field] = value
        if(field == 'data') {
            this.set('total', this.state[field].length);
        }
        return this
    }
    /**
     * (Method) State
     * @param state @type {{}}
     * @return void
     */
    setState(state) {
        this.state = Object.assign({}, this.state, state);
        if(this.isApiResource){
            //this.state = Object.assign({}, this.state, this.get('meta'));
            let meta = this.get('meta');
            Object.keys(meta).forEach( (key) => {
                this.set(key, meta[key]);
            });
            delete this.state.meta;
            delete this.state.links;
        }
    }

    /**
     * (Method) flush State
     * @return void
     */
    flush() {
        this.state = DefaultCollection
    }
    /**
     * (Method) Append Object
     * @param obj @type {{}}
     * @param dupes @type {Boolean}
     * @return {this}
     */
    append(obj, dupes = false) {
        if(!this.first(obj) || dupes){
            this.state.data.push(obj);
            let total = this.total;
            this.set('total', total+1);
        }
        return this
    }
    /**
     * (Method) Prepend Object
     * @param obj @type {{}}
     * @param dupes @type {Boolean}
     * @return {this}
     */
    prepend(obj, dupes = false) {
        if(!this.first(obj) || dupes){
            this.state.data.unshift(obj);
            let total = this.total;
            this.set('total', total+1);
        }
        return this
    }

    add(obj, index = null) {
        if(!this.has(obj)){
            if(index){
                this.state.data.splice(index, 0, obj);
                let total = this.total;
                this.set('total', total+1);
            }else{
                this.prepend(obj);
            }
        }
        return this
    }

    /**
     * (Method) Find Object by Property
     * @param obj @type {{*}}
     * @return {Boolean}
     */
    has(obj) {
        return this.state.data.indexOf(obj) >= 0
    }
    /**
     * (Method) Find Many Objects by Property
     * @param obj @type {Number|{}}
     * @param prop @type {Number|String}
     * @return {*}
     */
    find(obj, prop = 'id'){
        return this.state.data.filter((entry)=>obj[prop] === entry[prop]);
    }
    /**
     * (Method) Find Object by Property
     * @param obj @type {Number|{}}
     * @param prop @type {Number|String}
     * @return {*}
     */
    first(obj, prop = 'id'){
        return obj ? this.find(obj, prop)[0] : this.items[0] ? this.items[0]  : null;
    }
    /**
     * (Method) Update Object
     * @param obj @type {{}}
     * @param prop @type {String}
     * @return {this}
     */
    update(obj, prop = 'id') {
        const entries = this.find(obj, prop)
        if(entries){
            entries.forEach((entry)=>this.replace(entry, Object.assign({}, entry, obj)))
        }
        return this
    }
    /**
     * (Method) Remove Object
     * @param obj @type {{}}
     * @param prop @type {String}
     * @return {this}
     */
    remove(obj, prop = 'id') {
        const entries = this.find(obj, prop)
        if(entries){
            entries.forEach((entry)=>this.state.data.splice(this.state.data.indexOf(entry), 1))
            let total = this.total;
            this.set('total', total - entries.length);
        }
        return this
    }
    /**
     * (Method) Remove Object by Index
     * @param index @type {Number}
     * @return {this}
     */
    removeIndex(index) {
        let entry = this.item(index);
        if(entry){
            this.state.data.splice(this.state.data.indexOf(entry), 1)
            let total = this.total;
            this.set('total', total-1);
        }
        return this
    }
    /**
     * (Method) Append Object
     * @param entry @type {{*}}
     * @param obj @type {{*}}
     * @return {this}
     */
    replace(entry, obj) {
        this.state.data.splice(this.state.data.indexOf(entry), 1, obj)
        return this
    }
    /**
     * (Action) toggleSortable
     * @return void
     */
    toggleSortable(value) {
        if(this.isOrderedBy(value)){
            if(this.isSorted('desc')){
                this.set('sort', 'asc')
            }else{
                this.set('sort', 'desc')
            }
        }else{
            this.set('order_by', value)
            this.set('sort', 'desc')
        }
    }
    /**
     * (Action) toPreviousPage
     * @return void
     */
    previousPage() {
        this.page(this.get('current_page') - 1)
    }
    /**
     * (Action) toNextPage
     * @return void
     */
    nextPage() {
        this.page(this.get('current_page') + 1)
    }
    /**
     * (Action) page
     * @param value @type {Number}
     * @return void
     */
    page(value) {
        this.set('current_page', value)
    }
    /**
     * (Conditional Method) isCurrentPage
     * @param value @type {Number}
     * @return {Boolean}
     */
    isCurrentPage(value) {
        return this.get('current_page') === value
    }
    /**
     * (Conditional Method) isOrderedBy
     * @param value @type {Number}
     * @return {Boolean}
     */
    isOrderedBy(value) {
        return this.get('order_by') === value
    }
    /**
     * (Conditional Method) isSorted
     * @param value @type {Number}
     * @return {Boolean}
     */
    isSorted(value) {
        return this.get('sort') === value
    }
    /**
     * (Internal) buildLinks
     * @param currentPage @type {Number}
     * @param pageCount @type {Number}
     * @param delta @type {Number}
     * @return {Array}
     */
    buildLinks(currentPage, pageCount, delta = 3) {
        let range = []
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(pageCount - 1, currentPage + delta); i++) {
            range.push(i)
        }
        if (currentPage - delta > 2) {
            range.unshift("...")
        }
        if (currentPage + delta < pageCount - 1) {
            range.push("...")
        }
        range.unshift(1)
        range.push(pageCount)
        return range
    }
}
export {Collection}
export default {
    data() {
        return {
            collection: new Collection
        }
    },
}